/**
 * The checkbox list for the HTML cleaner, described as data.
 *
 * Keeping it here (rather than hand-writing markup) means adding an option is a
 * one-line change in two places: this list and `CleanOptions`.
 */

import type { CleanOptions } from '@/lib/htmlclean/types';

/** Every option in this list is a plain on/off toggle. */
export type SettingKey = {
  [K in keyof CleanOptions]: CleanOptions[K] extends boolean ? K : never;
}[keyof CleanOptions];

export interface SettingDef {
  key: SettingKey;
  label: string;
  hint: string;
}

export interface SettingGroup {
  title: string;
  options: SettingDef[];
}

export const SETTING_GROUPS: SettingGroup[] = [
  {
    title: 'Styling & attributes',
    options: [
      {
        key: 'stripInlineStyles',
        label: 'Strip inline styles',
        hint: 'Removes style="…" — colours, backgrounds, fonts, spacing.',
      },
      {
        key: 'stripClassesIds',
        label: 'Strip classes and IDs',
        hint: 'Removes class and id. A language-xxx class on <code> is kept.',
      },
      {
        key: 'stripDataAttrs',
        label: 'Strip data-* attributes',
        hint: 'Removes framework and tracking leftovers like data-wf or data-testid.',
      },
      {
        key: 'stripOtherAttrs',
        label: 'Strip other stray attributes',
        hint: 'Keeps only attributes that carry meaning (href, alt, colspan…) and drops the rest, like dir or align.',
      },
    ],
  },
  {
    title: 'Structure',
    options: [
      {
        key: 'unwrapSpans',
        label: 'Unwrap <span> and <font>',
        hint: 'Deletes the wrapper, keeps the words. This is what un-shreds text pasted from AI chats.',
      },
      {
        key: 'unwrapDivs',
        label: 'Turn <div> soup into paragraphs',
        hint: 'A div holding text becomes a <p>; a div holding other blocks just disappears.',
      },
      {
        key: 'semanticTags',
        label: 'Use semantic tags',
        hint: '<b> becomes <strong>, <i> becomes <em>, and duplicate nested tags are merged.',
      },
      {
        key: 'removeEmpty',
        label: 'Remove empty elements',
        hint: 'Deletes tags left with no content, like a stray empty <p>.',
      },
    ],
  },
  {
    title: 'Content',
    options: [
      {
        key: 'stripLinks',
        label: 'Strip links',
        hint: 'Removes <a> but keeps the link text.',
      },
      {
        key: 'stripImages',
        label: 'Strip images',
        hint: 'Removes <img>, <picture> and <figure>.',
      },
      {
        key: 'stripMedia',
        label: 'Strip embeds and media',
        hint: 'Removes iframes, video, audio, canvas and SVG.',
      },
      {
        key: 'stripFormElements',
        label: 'Strip buttons and form fields',
        hint: 'Removes forms, inputs, buttons, selects and labels.',
      },
      {
        key: 'stripTables',
        label: 'Strip tables',
        hint: 'Off by default — tables usually carry real content worth keeping.',
      },
      {
        key: 'stripComments',
        label: 'Strip HTML comments',
        hint: 'Removes <!-- … --> blocks.',
      },
    ],
  },
  {
    title: 'Text cleanup',
    options: [
      {
        key: 'convertNbsp',
        label: 'Convert &nbsp; to real spaces',
        hint: 'Non-breaking spaces are the main reason pasted text will not re-wrap properly.',
      },
      {
        key: 'collapseWhitespace',
        label: 'Tidy up spacing',
        hint: 'Collapses runs of spaces and line breaks, and trims each block. <pre> is left alone.',
      },
      {
        key: 'stripZeroWidth',
        label: 'Remove invisible characters',
        hint: 'Zero-width spaces, soft hyphens and byte-order marks that copy along invisibly.',
      },
      {
        key: 'normalizeQuotes',
        label: 'Straighten smart punctuation',
        hint: 'Curly quotes become straight, em dash becomes --, and … becomes three dots.',
      },
    ],
  },
];
